import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Minus } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuthStore } from '@/stores/auth';
import { useHelpGuidesStore } from '@/stores/helpGuides';
import { getAccessibleHelpGuides } from '@/features/helpGuides/catalog';
import type { HelpGuideDefinition } from '@/features/helpGuides/types';
import { useUIStore } from '@/stores/ui';

function ProgressRing({
  completed,
  total,
}: {
  completed: number;
  total: number;
}) {
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const progress = total > 0 ? completed / total : 0;
  const offset = circumference * (1 - progress);

  return (
    <div className="relative h-11 w-11">
      <svg width="44" height="44" viewBox="0 0 44 44" className="-rotate-90">
        <circle
          cx="22"
          cy="22"
          r={radius}
          stroke="#e5e7eb"
          strokeWidth="4"
          fill="none"
        />
        <circle
          cx="22"
          cy="22"
          r={radius}
          stroke="#90caf9"
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[12px] font-semibold text-[#4b5563]">
        {Math.round(progress * 100)}%
      </span>
    </div>
  );
}

interface GuideRowProps {
  guide: HelpGuideDefinition;
  completed: boolean;
  onStart: (guideId: HelpGuideDefinition['id']) => void;
  label?: string;
}

function GuideRow({ guide, completed, onStart, label }: GuideRowProps) {
  return (
    <button
      type="button"
      onClick={() => onStart(guide.id)}
      className="w-full rounded-lg px-2 py-1.5 text-left transition hover:bg-accent/30"
    >
      <div className="flex items-start gap-3">
        <div className="pt-0.5 shrink-0">
          {completed ? (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <Check className="h-3 w-3" />
            </span>
          ) : (
            <span className="flex h-5 w-5 items-center justify-center rounded-full border border-border text-muted-foreground">
              <Minus className="h-2.5 w-2.5" />
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-[0.88rem] font-semibold text-card-foreground leading-4.5">
            {label || guide.titleKey}
          </p>
        </div>
      </div>
    </button>
  );
}

export function HelpGuidesDrawer() {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const isOpen = useHelpGuidesStore((state) => state.isHelpGuideOpen);
  const closeHelpGuide = useHelpGuidesStore((state) => state.closeHelpGuide);
  const startGuide = useHelpGuidesStore((state) => state.startGuide);
  const getCompletedGuideIds = useHelpGuidesStore(
    (state) => state.getCompletedGuideIds,
  );
  const sidebarCollapsed = useUIStore((state) => state.sidebarCollapsed);

  const guides = useMemo(() => getAccessibleHelpGuides(user), [user]);
  const userId = user?.id || '';

  const completedIds = userId ? getCompletedGuideIds(userId) : [];
  const completedCount = guides.filter((guide) =>
    completedIds.includes(guide.id),
  ).length;
  const guideById = useMemo(
    () => new Map(guides.map((guide) => [guide.id, guide] as const)),
    [guides],
  );

  const sections = useMemo(
    () => [
      {
        titleKey: 'help_guides.sections.calendar',
        items: [
          {
            guideId: 'calendar_overview' as HelpGuideDefinition['id'],
            labelKey: 'help_guides.group_labels.calendar_first_access',
          },
          {
            guideId: 'calendar_create_scale' as HelpGuideDefinition['id'],
            labelKey: 'help_guides.group_labels.calendar_create_scale',
          },
        ],
      },
      {
        titleKey: 'help_guides.sections.scheduled_shift',
        items: [
          {
            guideId: 'scheduled_shift_register' as HelpGuideDefinition['id'],
            labelKey: 'help_guides.group_labels.scheduled_shift_register',
          },
        ],
      },
      {
        titleKey: 'help_guides.sections.records',
        items: [
          {
            guideId: 'records_management' as HelpGuideDefinition['id'],
            labelKey: 'help_guides.group_labels.records_management',
          },
        ],
      },
      {
        titleKey: 'help_guides.sections.my_establishment',
        items: [
          {
            guideId:
              'establishment_professionals_actions' as HelpGuideDefinition['id'],
            labelKey:
              'help_guides.group_labels.establishment_professionals_actions',
          },
          {
            guideId:
              'establishment_specialities_register' as HelpGuideDefinition['id'],
            labelKey:
              'help_guides.group_labels.establishment_specialities_register',
          },
          {
            guideId:
              'establishment_sectors_register' as HelpGuideDefinition['id'],
            labelKey: 'help_guides.group_labels.establishment_sectors_register',
          },
          {
            guideId:
              'establishment_document_management' as HelpGuideDefinition['id'],
            labelKey:
              'help_guides.group_labels.establishment_document_management',
          },
        ],
      },
    ],
    [],
  );

  const handleStartGuide = (guideId: HelpGuideDefinition['id']) => {
    if (!userId) return;
    closeHelpGuide();
    window.setTimeout(() => {
      startGuide(userId, guideId);
    }, 180);
  };

  if (!isOpen) return null;

  const panelLeft = sidebarCollapsed ? 82 : 268;

  return (
    <div className="fixed inset-0 z-[120]">
      <motion.button
        type="button"
        aria-label={t('common.close') || 'Fechar'}
        onClick={closeHelpGuide}
        className="absolute inset-0 bg-black/20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      />

      <div
        className="absolute right-0 flex justify-start"
        style={{
          left: panelLeft,
          bottom: 12,
          paddingInline: 10,
        }}
      >
        <motion.div
          className="rounded-[28px] border border-[#9cc8ff] bg-background shadow-[0_10px_28px_rgba(59,130,246,0.14)] flex flex-col w-full"
          style={{
            width: `min(400px, calc(100vw - ${panelLeft + 24}px))`,
            maxHeight: 'calc(100vh - 96px)',
          }}
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.24, ease: 'easeOut' }}
        >
          <div className="border-b bg-transparent px-5 py-4 relative">
            <button
              type="button"
              aria-label={t('common.close') || 'Fechar'}
              onClick={closeHelpGuide}
              className="absolute right-5 top-1/2 -translate-y-1/2 p-1 text-[#6b7280] hover:text-[#374151]"
            >
              <Minus className="h-5 w-5 hover:text-destructive" />
            </button>

            <div className="flex items-center gap-4 pr-10">
              <ProgressRing completed={completedCount} total={guides.length} />
              <div className="min-w-0">
                <p className="text-[1.2rem] font-semibold text-[#111827] leading-none">
                  {t('help_guides.title')}
                </p>
              </div>
            </div>
          </div>

          <div
            className="overflow-y-auto px-5 py-4"
            style={{ maxHeight: 'calc(100vh - 196px)' }}
          >
            <div className="space-y-3">
              {guides.length === 0 && (
                <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                  {t('help_guides.no_guides_available')}
                </div>
              )}

              {sections.map((section) => {
                const sectionItems = section.items
                  .map((item) => {
                    const guide = guideById.get(item.guideId);
                    if (!guide) return null;
                    return { guide, labelKey: item.labelKey };
                  })
                  .filter(Boolean) as Array<{
                  guide: HelpGuideDefinition;
                  labelKey?: string;
                }>;

                if (sectionItems.length === 0) return null;

                return (
                  <div key={section.titleKey}>
                    <p className="px-2 mb-0.5 text-[0.7rem] font-medium uppercase tracking-wide text-inativo">
                      {t(section.titleKey)}
                    </p>
                    <div className="space-y-0.5">
                      {sectionItems.map(({ guide, labelKey }) => (
                        <GuideRow
                          key={guide.id}
                          guide={guide}
                          completed={completedIds.includes(guide.id)}
                          onStart={handleStartGuide}
                          label={labelKey ? t(labelKey) : undefined}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
